import { type components, type GenomicFeatureTypeEnum, SyntenyTypeEnum } from "../openapi";
import type { RangePerContig } from "../store/slices/range-selection-slice.ts";
import { getClient } from "./openapi.ts";
import { throwErrors } from "./throw-errors.ts";

export async function getAlignedContigs(syntenyOrgId: number, orgId: number, syntenyType: SyntenyTypeEnum) {
  const client = getClient();
  const { data, error, response } = await client.GET(
    "/synteny/{syntenyOrgId}/componentGenome/{orgId}/{syntenyType}/alignedContigs",
    {
      cache: "no-cache",
      params: { path: { syntenyOrgId, orgId, syntenyType } },
    },
  );

  throwErrors(response, data, error);

  return data;
}

export async function fetchSyntenyInfo(orgId: number) {
  const client = getClient();
  const { data, error, response } = await client.GET("/genome/{orgId}/info", {
    cache: "no-cache",
    params: { path: { orgId } },
  });

  throwErrors(response, data, error);

  return data;
}

export async function fetchComponentGenomes(syntenyOrgId: number) {
  const client = getClient();
  const { data, error, response } = await client.GET("/genome/componentGenomes/{orgId}", {
    cache: "no-cache",
    params: { path: { orgId: syntenyOrgId } },
  });

  throwErrors(response, data, error);

  return data;
}

export async function fetchFeaturesFamilyMembersOrThrow(
  familyId: number,
  syntenyType: SyntenyTypeEnum,
  condense?: boolean,
) {
  const client = getClient();
  const endpoint =
    syntenyType === SyntenyTypeEnum.PROTEIN
      ? "/synteny/proteinFamily/{familyId}/members"
      : "/synteny/geneFamily/{familyId}/members";

  const { data, error, response } = await client.GET(endpoint, {
    cache: "no-cache",
    params: { path: { familyId }, query: { condense } },
  });

  throwErrors(response, data, error);

  return data;
}

export async function fetchPangenomeAlignmentMembersOrThrow(pangenomeOrgId: number, familyId: number) {
  const client = getClient();

  const { data, error, response } = await client.GET(
    "/synteny/{pangenomeOrgId}/pangenomeAlignment/{familyId}/members",
    {
      cache: "no-cache",
      params: { path: { pangenomeOrgId, familyId } },
    },
  );

  throwErrors(response, data, error);

  return data;
}

export async function fetchFamilyMembersCondensedOrThrow(
  syntenyOrgId: number,
  syntenyType: SyntenyTypeEnum,
  featureId: number,
) {
  const client = getClient();
  const endpoint = "/synteny/{syntenyOrgId}/{syntenyType}/feature/{featureId}/syntenyMembers";

  if (syntenyType !== SyntenyTypeEnum.PROTEIN && syntenyType !== SyntenyTypeEnum.GENE) return;

  const { data, error, response } = await client.GET(endpoint, {
    cache: "no-cache",
    params: { path: { syntenyOrgId, syntenyType, featureId }, query: { condense: true } },
  });

  throwErrors(response, data, error);

  return data;
}

export async function getFeatureTypes(contigId: number) {
  const client = getClient();
  const { data, error, response } = await client.GET("/contig/{contigId}/featureTypes", {
    cache: "no-cache",
    params: { path: { contigId } },
  });

  throwErrors(response, data, error);

  return data;
}

export async function fetchFeatureInfo(type: GenomicFeatureTypeEnum, featureId: number) {
  const client = getClient();
  const { data, error, response } = await client.GET("/feature/{type}/{featureId}/info", {
    cache: "no-cache",
    params: { path: { type, featureId } },
  });

  throwErrors(response, data, error);

  return data;
}

export async function getSequenceViewerSessionId(ranges: RangePerContig[]): Promise<string> {
  const client = getClient();
  const { data, error, response } = await client.POST("/sequenceAnalyzer/createSessionFromRanges", {
    cache: "no-cache",
    parseAs: "text",
    body: {
      ranges,
    },
  });

  throwErrors(response, data, error);

  return data;
}

export type SearchType = components["schemas"]["SearchGenomicEntityTypeRequest"];

export async function search(genomeId: number, searchTerm: string, types: SearchType[], signal: AbortSignal) {
  const client = getClient();
  const { data, error, response } = await client.POST("/search/genomicFeature", {
    cache: "no-cache",
    parseAs: "json",
    body: {
      genomeId,
      limit: 5,
      searchTerm,
      types,
    },
    signal,
  });

  throwErrors(response, data, error);

  return data;
}

export async function fetchFeatureData(resultType: GenomicFeatureTypeEnum, featureId: number) {
  const client = getClient();
  const { data, error, response } = await client.GET("/feature/{type}/{featureId}/locations", {
    cache: "no-cache",
    params: {
      path: { type: resultType, featureId },
    },
  });

  throwErrors(response, data, error);

  return data;
}
